import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, Send as SendIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

/**
 * NotificationSettings (MONITORING_SPEC.md §8.1).
 *
 * 운영 모니터링 알림 설정:
 *   - 수신 이메일 추가/삭제
 *   - 매일 다이제스트 ON/OFF + 발송 시각 (KST)
 *   - ESCALATED 즉시 알림 ON/OFF
 *   - 대상 테넌트 (전체 / 일부)
 *
 * 백엔드 API:
 *   GET  /api/notifications/settings
 *   PUT  /api/notifications/settings
 *   POST /api/notifications/digest/run  (수동 발송 테스트)
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const NotificationSettings = () => {
  const { idToken } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/notifications/settings', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      toast.error(`설정 로딩 실패: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idToken) fetchSettings();
  }, [idToken]); // eslint-disable-line

  const updateField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const addRecipient = () => {
    const e = newEmail.trim();
    if (!EMAIL_RE.test(e)) {
      toast.error('올바른 이메일 형식이 아닙니다');
      return;
    }
    if (settings.recipients.includes(e)) {
      toast.error('이미 등록된 이메일입니다');
      return;
    }
    updateField('recipients', [...settings.recipients, e]);
    setNewEmail('');
  };

  const removeRecipient = (e) => {
    if (settings.recipients.length === 1) {
      toast.error('최소 1명의 수신자가 필요합니다');
      return;
    }
    updateField('recipients', settings.recipients.filter((x) => x !== e));
  };

  const onSave = async () => {
    if (!TIME_RE.test(settings.digest_time_kst)) {
      toast.error('발송 시각은 HH:MM 형식이어야 합니다');
      return;
    }
    if (settings.recipients.length === 0) {
      toast.error('수신자가 최소 1명 필요합니다');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/notifications/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          recipients: settings.recipients,
          digest_enabled: settings.digest_enabled,
          digest_time_kst: settings.digest_time_kst,
          escalated_enabled: settings.escalated_enabled,
          tenant_filter_all: settings.tenant_filter_all,
          tenant_filter_ids: settings.tenant_filter_ids,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setSettings(updated);
      toast.success('알림 설정 저장됨');
    } catch (err) {
      toast.error(`저장 실패: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onTestDigest = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/notifications/digest/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ force: true }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      if (body.skipped) {
        toast(`다이제스트 스킵됨: ${body.reason}`);
      } else if (body.sendResult?.delivered) {
        toast.success(`다이제스트 발송됨 → ${body.recipients.join(', ')}`);
      } else {
        toast(`다이제스트 처리됨 (mode=${body.sendResult?.mode || 'unknown'})`);
      }
    } catch (err) {
      toast.error(`테스트 발송 실패: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  if (loading || !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          알림 설정
        </Typography>
        <Typography variant="body2" color="text.secondary">
          운영 모니터링 다이제스트 + ESCALATED 즉시 알림 (MONITORING_SPEC.md §8)
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          수신자
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {settings.recipients.map((e) => (
            <Chip
              key={e}
              label={e}
              onDelete={() => removeRecipient(e)}
              deleteIcon={<DeleteIcon />}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            placeholder="recipient@aify.ai"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
            sx={{ flex: 1 }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addRecipient}>
            추가
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          매일 다이제스트
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.digest_enabled}
              onChange={(e) => updateField('digest_enabled', e.target.checked)}
            />
          }
          label="활성"
          sx={{ mb: 2 }}
        />
        <Box>
          <TextField
            size="small"
            label="발송 시각 (KST)"
            type="time"
            value={settings.digest_time_kst}
            onChange={(e) => updateField('digest_time_kst', e.target.value)}
            disabled={!settings.digest_enabled}
            inputProps={{ step: 300 }}
            sx={{ width: 200 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          매일 {settings.digest_time_kst} KST 에 어제 BSDA 의 8 테넌트 요약을 메일 발송합니다.
        </Alert>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          ESCALATED 즉시 알림
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.escalated_enabled}
              onChange={(e) => updateField('escalated_enabled', e.target.checked)}
            />
          }
          label="활성 (fix_loop max_retries 3 도달 시)"
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          대상 테넌트
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={settings.tenant_filter_all}
              onChange={(e) => updateField('tenant_filter_all', e.target.checked)}
            />
          }
          label="전체 8 테넌트"
        />
        {!settings.tenant_filter_all && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="테넌트 ID (쉼표 구분)"
              placeholder="c8cd350,c373710"
              value={(settings.tenant_filter_ids || []).join(',')}
              onChange={(e) =>
                updateField(
                  'tenant_filter_ids',
                  e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                )
              }
            />
          </Box>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? '저장 중…' : '저장'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={onTestDigest}
          disabled={sending}
        >
          {sending ? '발송 중…' : '지금 다이제스트 발송 (테스트)'}
        </Button>
      </Stack>

      {settings.updated_at && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          마지막 수정: {new Date(settings.updated_at).toLocaleString('ko-KR')} ·{' '}
          {settings.updated_by || 'unknown'}
        </Typography>
      )}
    </Box>
  );
};

export default NotificationSettings;
